/**
 * useCdDetail — React wrapper around `CdInqService.fetchCdDetail`.
 *
 * - Abort-capable: a fresh AbortController per `acctId` change, and
 *   unmount tears down in-flight requests.
 * - Exposes `reload()` for officer-initiated refresh after mutations
 *   (waiver recorded → rate recomputed, maturity saved → echo back).
 * - Mirrors the surface of `useCif` in `modules/RCM/hooks/useCif.ts`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { CdInqService } from '../services/CdInqService';
import type { CdDetail } from '../types';

export interface UseCdDetailResult {
  data: CdDetail | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useCdDetail(acctId: string | undefined): UseCdDetailResult {
  const client = useApiClient();
  const abortRef = useRef<AbortController | null>(null);
  const [tick, setTick] = useState<number>(0);

  const [data, setData] = useState<CdDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(!!acctId);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!acctId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    // Cancel any in-flight request from a previous `acctId` or reload.
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setError(null);

    const svc = new CdInqService(client);
    void (async (): Promise<void> => {
      try {
        const detail = await svc.fetchCdDetail(acctId, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        setData(detail);
        setLoading(false);
      } catch (err) {
        if (ctrl.signal.aborted) return;
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Failed to load CD detail.');
        setLoading(false);
      }
    })();

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acctId, client, tick]);

  const reload = useCallback((): void => setTick((t) => t + 1), []);

  return { data, loading, error, reload };
}
